// Manual do Aluno. Markdown simples (ver components/MarkdownLite.jsx): sem crases nem ${}.
export const manualAluno = `# Manual do Aluno

## 1. Primeiros passos

Para começar, entras com a tua conta Google, escolhes a escola e tens 7 dias de teste grátis para experimentar a plataforma.

1. Abre o endereço da plataforma e carrega em **Entrar**.
2. Autentica-te com a tua conta Google. Não precisas de criar palavra-passe.
3. Se ainda não estás associado a nenhuma escola, aparece o aviso "Ainda não estás associado a nenhuma escola". Escolhe a tua escola e envia o pedido.
4. Quando a escola aprovar o pedido, o instrutor ou o administrador coloca-te numa turma e desbloqueia as disciplinas que deves treinar.

**Período de teste.** Tens 7 dias grátis. Durante o teste só a disciplina de Legislação está disponível. Quando termina, aparece "O seu período de teste terminou" e passas a precisar de uma subscrição (ver a secção 8).

## 2. O seu painel

O **Painel do Aluno** é o ecrã inicial: junta o treino, os exames, o progresso e os prémios num só sítio. Se a tua conta estiver ligada a uma ficha de piloto, aparece o botão **Reservar voo** na barra do topo: escolhes o dia, o tipo (voo, instrução ou passageiro) e um horário livre de 1 hora, em UTC. As aeronaves bloqueadas mostram o motivo e não aceitam reservas. Reservas-te com 2 horas de antecedência, até 30 dias à frente, e podes cancelar até 2 horas antes. Se tiveres um documento em falta, sem data ou expirado, só podes reservar como passageiro. O **sino** no topo avisa-te dos documentos a expirar ou em falta, dos registos de voo recusados pelo DI e das reservas que o DI cancelou; o número mostra os avisos que ainda não viste. No topo do painel vês os **Próximos voos** e a **Instrução prática** (duplo comando e horas a solo, com os objetivos de 22 h e 8 h, só em aeronaves ultraleve). Fecham-se e abrem-se com um toque; fechados mostram só o resumo. As **Teóricas ANAC** também se fecham: fechadas mostram quantos cadernos estão aprovados e a data do próximo exame.

1. **Cartões no topo:** *Dias seguidos*, *Respostas corretas*, *Sessões*, *Badges* e o teu nível por XP (por exemplo, "Top Gun").
2. **Iniciar Treino:** carrega em **Treinar agora** para começar uma sessão (secção 3).
3. **Exames:** se ainda não estás numa turma, vês "Sem Turma Atribuída" e o conselho de falar com o instrutor. Quando estás numa turma, vês aqui os exames, com o estado de cada um (secção 5).
4. **Skill Rating:** o teu valor, a variação nos últimos 7 e 30 dias, o número de exames e de avaliações feitos, e um gráfico da evolução. Passa o rato (ou o dedo) num ponto para ver o detalhe (secção 4).
5. **Tempo no aeródromo:** as condições meteorológicas do aeródromo da tua escola, por exemplo VFR, nebulosidade e vento.
6. **Progresso por Disciplina:** uma linha por disciplina, com a percentagem de progresso, quantas perguntas já dominas e quantas já tentaste (secção 6). **Ver detalhes** mostra Cobertura, Domínio e Estabilidade.
7. **Badges, Missões e Ranking:** um resumo de cada um, com ligação para o ecrã completo (secção 7).
8. **Teóricas ANAC:** o **Próximo exame** aparece em destaque no topo, com os dias que faltam. A tabela das teóricas (Caderno I, Caderno II e Comunicações) está logo abaixo (ver a secção seguinte). Só o instrutor regista e corrige.

![O topo do Painel do Aluno: dias seguidos, respostas corretas, sessões, badges e nível, e o cartão Iniciar Treino](/help/aluno-painel.jpg)

Se vires "Comece a treinar para ver o seu progresso", é normal: ainda não respondeste a perguntas. O progresso aparece depois da primeira sessão.

## 3. Treino

O treino adapta-se a ti: escolhe as perguntas que mais te ajudam e, depois de cada resposta, mostra se acertaste e a explicação.

### Como começar uma sessão

1. No painel, carrega em **Iniciar Treino**.
2. Em **Configure a sua sessão**, escolhe **Treinar por** *Disciplina* ou *Caderno*.
3. Escolhe qual, ou deixa "Todas as Disciplinas" / "Todos os Cadernos" para treinar tudo o que tens desbloqueado.
4. Define o **Número de Questões** (por defeito, 10).
5. Responde. No fim, a **Revisão da Sessão** mostra todas as perguntas, as tuas respostas e as corretas.

![O ecrã Treino Adaptativo: Treinar por Disciplina ou Caderno, número de questões e a caixa Modo Avaliação](/help/aluno-treino.jpg)

Se achares que uma pergunta está errada, usa **Reportar Questão**. Só podes treinar as disciplinas que o teu instrutor desbloqueou.

### Que perguntas saem

Não são escolhidas ao acaso. Cada sessão mistura três grupos:

| Grupo | O que é | Peso na sessão |
| --- | --- | --- |
| Novas | Perguntas que nunca viste | 40% |
| Erradas | Perguntas cuja última resposta foi errada | 30% |
| Revisão | Perguntas que já acertaste, para consolidar | 30% |

Dentro de cada grupo, a plataforma dá prioridade às que viste há mais tempo e evita repetir as das últimas 3 sessões. No fim, a ordem é baralhada. Se um grupo não tiver perguntas suficientes, o que falta é completado com perguntas de revisão.

### Quando já dominas a matéria: modo manutenção

Quando já viste **todas** as perguntas de uma disciplina e dominas **95% ou mais**, essa disciplina passa a **modo manutenção** (aparece com esse rótulo). Deixa de haver "novas" e a sessão serve para manter o nível:

- a maior parte das perguntas é sorteada entre as que viste há mais tempo (quanto mais antiga, mais provável);
- até 10% da sessão são perguntas que erraste nas últimas 48 horas, para as acertares de vez.

Por isso, mesmo com tudo a verde, algumas perguntas que falhaste podem voltar nas sessões seguintes: é de propósito.

## 4. Modo Avaliação

O Modo Avaliação mede o teu nível a sério: as perguntas são sorteadas, não há explicação entre perguntas e o resultado altera o teu **Skill Rating**. O treino normal não o altera.

**Como ativar:** ao configurar a sessão, marca a caixa **Modo Avaliação**.

| Regra | Valor por defeito |
| --- | --- |
| Número mínimo de perguntas | 15 |
| Âmbito | Uma disciplina ou um caderno específico (não "todas") |
| Sessões de avaliação por 24 horas | 3 |
| Espera entre sessões da mesma disciplina ou caderno | 30 minutos |
| Feedback entre perguntas | Nenhum (vês tudo no fim) |

Estes valores podem ser alterados pela administração da plataforma. Se tentares uma sessão fora das regras, a plataforma explica o motivo.

**Skill Rating.** Começas com 1000 pontos. Sobe quando acertas acima de 75% e desce quando ficas abaixo. Conta nas sessões de avaliação (com peso menor) e nos exames oficiais (com peso maior), e nunca desce abaixo de 0. A evolução fica guardada e vais vê-la numa linha temporal.

**Quando usar.** Usa o treino normal para aprender e o Modo Avaliação para testar-te, por exemplo antes de um exame.

## 5. Exames

Os exames são criados pelo teu instrutor para a tua turma. Aparecem no painel, no cartão **Exames**, agrupados em **Exames de Preparação** e **Exames Simulados**. Cada exame passa por quatro momentos:

| Onde aparece | O que vês | O que fazes |
| --- | --- | --- |
| Agendados (aguardam início) | O exame com um cadeado e "Aguarda início" | Esperar que o instrutor o inicie |
| Disponíveis agora | O tempo que falta, a contar para trás, e o botão **Entrar** | Entrar e fazer o exame |
| Concluídos, "A aguardar correção" | Um relógio | Esperar que o instrutor liberte a correção |
| Concluídos, com resultado | A percentagem e "Aprovado" ou "Reprovado", e um ícone de olho | Carregar no olho para rever a correção |

![Exame agendado: aguarda início](/help/aluno-ex-agendado.jpg)

![Exame disponível agora, com a contagem do tempo e o botão Entrar](/help/aluno-ex-disponivel.jpg)

### Como fazer um exame

1. Em **Disponíveis agora**, carrega em **Entrar**. O tempo começa a contar.
2. Lê a pergunta e escolhe uma opção (A, B, C…). A opção escolhida fica destacada e podes mudar de ideias antes de passares à seguinte.
3. Usa **Anterior** e **Próxima** para navegar. O topo mostra "Questão 3 de 10", o tempo que resta e uma barra que fica verde nas perguntas já respondidas.
4. Na última pergunta, o botão passa a **Submeter Exame**. A plataforma pede confirmação: "Tem a certeza que deseja submeter o exame? Esta ação não pode ser revertida."

![Uma pergunta do exame, com a barra de progresso e o tempo restante](/help/aluno-ex-exame.jpg)

Depois de submeteres, vês "Exame Submetido!" e o aviso "A aguardar correção pelo instrutor". Se não submeteres, o sistema fecha o exame sozinho quando acaba o tempo e usa as respostas que já tinhas dado. A ordem das perguntas e das respostas é baralhada para cada aluno.

![Ecrã depois de submeter o exame](/help/aluno-ex-submetido.jpg)

### Aprovação e o que conta

- **A correção não é imediata.** Só a vês quando o instrutor termina o exame e desbloqueia as revisões. Até lá, o exame fica em "A aguardar correção".
- **A revisão** (o ícone de olho) mostra a percentagem, se foste aprovado ou reprovado e, pergunta a pergunta, a tua resposta, a resposta correta e se acertaste. Podes carregar em **Pedir explicação à IA**, que tem um limite diário (o ecrã mostra, por exemplo, "10/10 hoje"). No fim vês os resultados por disciplina.
- Passas com **75% ou mais** de respostas certas (valor por defeito). O mesmo limiar aplica-se a cada disciplina.
- Os **exames de preparação** não contam para médias, ranking, progresso nem Skill Rating: servem só para praticares.
- Os **exames oficiais** contam para tudo isso e têm mais peso no Skill Rating.

![O resultado de um exame de preparação](/help/aluno-ex-resultado.jpg)

![A revisão do exame: resposta certa, a tua resposta e o resultado](/help/aluno-ex-revisao.jpg)

### As minhas teóricas ANAC

No topo há **um card por caderno** (Caderno I, Caderno II e Comunicações), com o estado de cada um: **Aprovado**, **Marcado**, **Reprovado: repetir**, **Caducado: repetir**, **Faltou**, **Sem tentativas** ou **Por fazer**. Por baixo, o resumo diz quantos cadernos faltam ou, quando estão os três aprovados, que as **teóricas estão completas** e até quando valem para a licença.

A tabela mostra cada caderno ou comunicação, com a **época** (o mês e o ano do exame, em formato numérico, por exemplo «09/2026»), a data, a nota e o resultado. Em telemóvel, a época e a data aparecem por baixo do nome do caderno. O resultado é **Aprovado** com 75% ou mais; abaixo disso fica **Reprovado** e tens de repetir o exame.

Também vês:
- **Sittings:** quantas épocas já usaste, de 6. Uma época é o mês de exame: vários exames no mesmo mês são uma só época.
- **Faltou:** conta como ida, mas não gasta uma das 4 tentativas.
- **Justificado (ANAC):** falta justificada com atestado médico aprovado pela ANAC. Não conta.
- **Prazo para completar as provas:** 12 meses a contar do fim do mês da primeira prova. Se passar sem estarem todas aprovadas, as aprovadas caducam e tens de repetir tudo.
- **Validade das provas para a licença:** 24 meses depois do fim do mês da última prova.
- **Fim do prazo do curso:** o curso tem de estar terminado, com o exame prático, até 2 anos depois do início da tua turma.
- **Cartão de aluno:** a validade é a que consta no próprio cartão. Envia o cartão no separador Documentos e validades, e o DI aprova a data.

Só o instrutor regista e corrige estas informações. Se alguma data estiver errada, fala com ele.

## 6. Progresso e domínio

Para cada disciplina, o painel mostra o quanto já sabes. O progresso combina três indicadores e só sobe de verdade quando viste, acertaste e consolidaste.

| Indicador | O que mede |
| --- | --- |
| Cobertura | A percentagem de perguntas que já viste pelo menos uma vez |
| Domínio | A percentagem de perguntas que dominas |
| Estabilidade | O quão consolidadas estão essas respostas |

O **progresso final** é Cobertura × Domínio × Estabilidade. Nenhum indicador chega sozinho.

**Pergunta dominada.** Uma pergunta está dominada quando acertaste **pelo menos 2 vezes, em dias diferentes**. Se a última resposta for errada, deixa de estar dominada. Isto evita contar como aprendida uma pergunta que acertaste por sorte num só dia.

**Estabilidade.** Sobe com acertos seguidos: 3 seguidos dão o máximo. Não depende do tempo que passou desde a última vez.

### As cores e os rótulos

A percentagem que vês junto a cada disciplina é o progresso (o produto dos três indicadores). Muda de cor e de rótulo:

| Progresso | Cor | Rótulo |
| --- | --- | --- |
| 90% ou mais | Verde | Dominado |
| 75% a 89% | Verde | Bom |
| 50% a 74% | Amarelo | Em progresso |
| 25% a 49% | Laranja | A melhorar |
| Menos de 25% | Laranja | Iniciante |
| Ainda sem respostas | Cinzento | Por começar |

Quando já viste todas as perguntas e dominas 95% ou mais, aparece o selo **Modo manutenção** (secção 3).

![Progresso por Disciplina: percentagem, rótulo, perguntas dominadas e tentadas](/help/aluno-progresso.jpg)

## 7. Missões, badges, XP e ranking

Esta parte é opcional e serve para te manteres motivado. Nada aqui altera a matéria nem a nota dos exames.

**Missões.** Objetivos que dão XP quando os cumpres. No ecrã *Missões* há três tipos:

| Tipo | Quando reinicia | Exemplos |
| --- | --- | --- |
| Diárias | À meia-noite | Sortida Matinal (uma sessão entre as 3h e as 7h, 30 XP); Operação Noturna (uma sessão entre as 21h e as 3h, 30 XP); Aterragem Perfeita (uma sessão com 100% de acerto, 75 XP) |
| Semanais | Todas as segundas-feiras | Treino Maverick (5 sessões, 100 XP); Ataque de Precisão (50 respostas certas, 150 XP); Zona de Perigo (80% de precisão em 3 disciplinas, 200 XP); Ice Man (7 dias seguidos a treinar, 250 XP); Goose – Asa Fiel (100 questões, 175 XP) |
| Especiais | Objetivos únicos | Ás Top Gun (passar num exame simulado com 90% ou mais, 500 XP); Mestre da Escola de Voo (75% ou mais em todas as disciplinas, 1000 XP); Piloto do Século (100 sessões, 750 XP) |

O XP total dá-te um título de nível, que aparece no painel e no ecrã das missões.

**Badges.** São 13 e são atribuídos automaticamente. Na página *Badges* vês quantos já tens (por exemplo, 11/13) e a data de cada conquista.

| Grupo | Badge e como se ganha |
| --- | --- |
| Marcos | Primeiro Voo: registo na plataforma |
| Treino | Descolagem: primeira sessão de treino |
| Consistência | Voo Regular (3 dias seguidos), Piloto Dedicado (7 dias seguidos), Veterano (30 dias seguidos) |
| Precisão | Bom Começo (50 respostas certas), Precisão (100), Ás (500) |
| Especiais | Madrugador (sessão antes das 7h), Coruja Noturna (sessão depois das 21h) |
| Conquistas | Voo Perfeito: sessão de treino com 100% de acerto |
| Exames | Aprovado: passar num exame simulado |
| Mestria | Top Gun: 90% ou mais em todas as disciplinas |

Os badges de dias seguidos contam dias consecutivos de treino: se falhares um dia, a contagem recomeça.

**XP semanal.** Ganhas pontos pelo que fazes na semana:

| Atividade | XP base | Bónus |
| --- | --- | --- |
| Treino | 2 por pergunta | +5 com 10 ou mais perguntas; +10 com 25 ou mais; +5 com 80% de acertos; +10 com 90% |
| Exame | 3 por pergunta | +10 com 80% de acertos; +20 com 90% |

Os exames de preparação não dão XP.

**Ranking.** O ecrã *Ranking* tem quatro separadores: **XP** (o XP ganho nos últimos 7 dias), **Skill** (o Skill Rating), **Evol.** (a evolução semanal) e **Class.** (a classificação clássica). Mostra a tua posição (por exemplo, "A sua posição #14 de 14 participantes") e o Top 50. Para os alunos, a comparação é com os colegas da tua escola. A classificação clássica combina quatro coisas: atividade (quanto respondes), qualidade (a tua precisão), consistência (dias seguidos a treinar) e exames aprovados. Se ainda não treinaste, vês "Treine para entrar no ranking!".

**Evolução.** Compara a tua precisão dos últimos 7 dias com a dos 7 dias anteriores, em pontos percentuais. Por exemplo, +8 significa 8 pontos acima.

## 8. Subscrição, conta e privacidade

### Subscrição

- **Teste grátis:** 7 dias, com a disciplina de Legislação. Na página de Subscrição vês "Teste grátis até (data)" e os dias que restam.
- **Depois do teste:** precisas de uma subscrição ativa para continuar. O preço mensal está indicado na página de Subscrição.
- **Subscrever:** carrega em **Subscrever Agora**. O pagamento é feito num ecrã seguro do Stripe, com cartão de crédito ou débito (Visa, Mastercard ou American Express). A plataforma não vê nem guarda os dados do teu cartão.
- **Gerir a subscrição:** depois de subscrever, **Gerir Subscrição** abre o portal do Stripe, onde alteras o cartão, vês as faturas e cancelas. Quando acaba o período pago, a página mostra "Válido até (data)".
- **Acesso dado pela escola:** se a tua escola te deu acesso, não precisas de subscrever.

### Conta

- Entras sempre com a tua conta Google. Podes corrigir o teu nome no painel (ícone de edição junto ao nome).
- Podes ativar ou desativar as notificações no dispositivo. Os e-mails de lembrete têm uma ligação para deixares de os receber.
- Podes instalar a plataforma como aplicação no telemóvel, através do aviso de instalação do navegador.

### Privacidade

Os teus resultados são vistos por ti e pelos instrutores e administradores da tua escola. Os alunos de outras escolas não têm acesso aos teus dados.

## 9. Perguntas frequentes

| Problema | O que fazer |
| --- | --- |
| Não consigo escolher uma disciplina | Está bloqueada. Pede ao teu instrutor que a desbloqueie. No período de teste só a Legislação está disponível. |
| Aparece "O seu período de teste terminou" | Os 7 dias acabaram. Subscreve na página de Subscrição (secção 8). |
| Aparece "Ainda não estás associado a nenhuma escola" | Escolhe a tua escola e envia o pedido. Falta a aprovação da escola. |
| Não vejo nenhum exame | Os exames são criados pelo instrutor para a tua turma. Confirma que estás numa turma. |
| Vejo sempre as mesmas perguntas | Se já dominas a disciplina (modo manutenção), algumas perguntas que falhaste nas últimas 48 horas voltam de propósito até as acertares. As restantes mudam de sessão para sessão. Se ainda assim te parecer demais, fala com o teu instrutor. |
| Uma pergunta parece estar errada | Usa **Reportar Questão** durante o treino. A equipa revê-a. |
| O meu Skill Rating não mexeu | Só muda com o Modo Avaliação e com exames oficiais, não com o treino normal nem com exames de preparação. |
| Fiz uma sessão e não a vejo concluída | Uma sessão só conta quando chegas ao fim. Se saíres a meio, começa outra. |
| Fui desligado da plataforma | Volta a carregar em **Entrar** com a tua conta Google. |

Se o problema continuar, fala com o teu instrutor ou com o administrador da tua escola.
`;
